import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { ActionReviewCard } from "@/accounts-payable/invoice-review-page/action-review-card";
import {
  useBulkApproveAgentActions,
  useGetDocumentAgentRuns,
  useResolveAgentAction,
  useUpdateAgentAction,
} from "@/accounts-payable/query";
import type { ApDocumentType } from "@/accounts-payable/types";
import {
  AUTOMATED_RUN_BADGE,
  getActiveRun,
  getLatestCompletedRun,
  getBulkApproveIds,
  runsInFlightRefetchInterval,
  isAutomatedRun,
  isBulkApprovable,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { CollapsibleGroup } from "./collapsible-group";
import { AgentRunningNotice, StatusBadge } from "./shared";
/** The agent's proposals for one purchase document, reviewable in place. */
export const DocumentReviewActions = ({
  documentId,
  documentType,
  documentHasJob,
  businessId,
}: {
  documentId: string;
  documentType: ApDocumentType;
  /** Whether the document has a job, whose POs a PO pick opens on. */
  documentHasJob: boolean;
  businessId: string | null;
}) => {
  // Poll every 3s while the runs are in flight, so cards reach their terminal
  // state without a manual refresh.
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useGetDocumentAgentRuns(
    businessId,
    documentType,
    documentId,
    runsInFlightRefetchInterval,
  );
  const resolve = useResolveAgentAction(businessId);
  const update = useUpdateAgentAction(businessId);
  const bulkApprove = useBulkApproveAgentActions(businessId);

  const run = getLatestCompletedRun(runs);
  const activeRun = getActiveRun(runs);
  const actions = run?.actions ?? [];
  const pendingActions = actions.filter(isPendingReviewAction);
  // "Approve all" is meaningless when the rest are acknowledgements (the
  // Review tab's rule); it settles every pending row, flags included, except
  // a blocked PO, which stays pending with its reason.
  const approvableCount = pendingActions.filter(isBulkApprovable).length;
  const resolvedActions = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack gap="md" fullWidth>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap="md"
        fullWidth
        className="flex-wrap"
      >
        <Stack direction="row" gap="sm" align="center" className="flex-wrap">
          <Typography variant="subtitleW2">
            Agent actions{actions.length ? ` (${actions.length})` : ""}
          </Typography>
          {run && isAutomatedRun(run) && (
            <StatusBadge meta={AUTOMATED_RUN_BADGE} />
          )}
        </Stack>
        {approvableCount > 1 && (
          <Button
            size="xsPill"
            variant="tertiary"
            loading={bulkApprove.isPending}
            onClick={() => bulkApprove.mutate(getBulkApproveIds(actions))}
          >
            Approve all
          </Button>
        )}
      </Stack>
      {/*
            Above the list, not instead of it: a re-match leaves the previous
            run's proposals on screen while the new run works, and those are
            about to be superseded. Without a standing notice the page looks
            settled and the stale proposals look current.
          */}
      {activeRun && <AgentRunningNotice />}
      {actions.length > 0 ? (
        // Open work leads; resolved collapses while anything is pending.
        <Stack gap="lg" fullWidth>
          {[
            {
              label: "Needs review",
              items: pendingActions,
              defaultOpen: true,
            },
            {
              label: "Resolved",
              items: resolvedActions,
              defaultOpen: pendingActions.length === 0,
            },
          ].map((group) => (
            <CollapsibleGroup
              // Keyed by document so stepping through the queue resets a
              // manual open/close choice.
              key={`${documentId}-${group.label}`}
              label={group.label}
              count={group.items.length}
              defaultOpen={group.defaultOpen}
            >
              <Stack
                gap="none"
                fullWidth
                className="divide-y divide-stroke-tertiary overflow-hidden rounded-lg border border-stroke-tertiary bg-card"
              >
                {group.items.map((a) => (
                  <ActionReviewCard
                    key={a.id}
                    action={a}
                    runActions={actions}
                    businessId={businessId}
                    document={{
                      id: documentId,
                      type: documentType,
                      agentRunId: (activeRun ?? run)?.id ?? null,
                      hasJob: documentHasJob,
                    }}
                    onResolve={(decision) =>
                      resolve.mutate({ actionId: a.id, decision })
                    }
                    onSave={(payload) =>
                      update.mutate({ actionId: a.id, payload })
                    }
                    resolving={resolve.isPending}
                    saving={update.isPending}
                  />
                ))}
              </Stack>
            </CollapsibleGroup>
          ))}
        </Stack>
      ) : runsError ? (
        // Say the load failed rather than claim there are none (the
        // sidebar's rule); a still-loading list says nothing yet.
        <Typography textColor="tertiary">
          Couldn’t load agent actions. Please try again.
        </Typography>
      ) : (
        !activeRun &&
        !runsLoading && (
          <Typography textColor="tertiary">
            No agent actions for this {documentType} yet.
          </Typography>
        )
      )}
    </Stack>
  );
};
